import React, {useImperativeHandle, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectItem} from 'helpers/hooks/utils';
import StyledAdvancedQuery from 'components/organisms/Queries/AdvancedQuery/AdvancedQuery.styles';
import AdvancedQueryPart from 'components/organisms/Queries/AdvancedQueryPart/AdvancedQueryPart';
import utils from 'helpers/utils';
import constants from 'helpers/constants';

const AdvancedQuery = React.forwardRef((props, ref) => {
  const {
    query,
    terms,
    wizard,
    examples,
    similarity,
    filterGroups,
    initialised,
    fieldData,
    required,
    readOnly,
    disabled,
    error,
    success,
    size,
    actions,
    onChange,
    onError,
    ...innerProps
  } = useComponentProps({
    size: 'small',
    initialised: true,
    ...props
  }, 'AdvancedQuery');

  const innerRef = useRef(null);
  const partRef = useRef(null);
  const openFilterRef = useRef(false);

  const termsMemo = useEffectItem(terms);
  const examplesMemo = useEffectItem(examples);
  const canUpdate = Boolean(!disabled && !readOnly);

  const advancedQuery = useMemo(() => ({
    refs: {
      ref: innerRef,
      partRef
    },
    submit: (touch = true) => {
      return partRef.current?.submit?.(touch);
    },
    validate: (touch = true) => {
      return partRef.current?.validate?.(touch);
    },
    reset: (state) => {
      return partRef.current?.reset?.(state);
    }
  }), []);

  useImperativeHandle(ref, () => advancedQuery);

  const filterGroupsMemo = useMemo(() => {
    const processFilter = (filter) => {
      // does the filter have a list of options or user-input allowed
      const staticOptions = !utils.isEmpty(filter.options);
      const defaultOperators = staticOptions ? constants.query.filterOperatorGroups.static :
        constants.query.filterOperatorGroups.dynamic;

      return {
        ...filter,
        staticOptions,
        operators: (filter.operators ?? defaultOperators)
      }
    }

    const entityFilterGroups = [];
    if (filterGroups) {
      filterGroups
        .forEach((filterGroupDef) => {
          entityFilterGroups.push({
            ...filterGroupDef,
            name: filterGroupDef.name,
            title: filterGroupDef.title,
            filters: filterGroupDef.filters?.reduce((a, filter) => {
              return a.concat([processFilter(filter)]);
            }, [])
          });
        });
    }

    return entityFilterGroups;
  }, [filterGroups]);

  const handleChange = (change, isNewPart) => {
    const cleanNewParts = (part, depth = 0) => {
      if (part.parts) {
        part.parts = part.parts.filter((p) => !p.isNew);
        part.parts.forEach((p) => {
          cleanNewParts(p, depth + 1);
        });

        if (depth > 0 && part.parts.length === 1) {
          Object.assign(part, {
            ...part,
            ...part.parts[0],
            id: part.id,
            queryType: part.queryType,
            parts: part.parts[0].parts
          });
        }
      }

      if (utils.isEmpty(part.parts)) {
        delete part.parts;
      }
    }

    if (!isNewPart) {
      const newQuery = utils.clone(query, true);
      cleanNewParts(change);
      if (change.parts?.length > 0) {
        newQuery.parts = change.parts;
      } else if (utils.isDefined(change.queryType)) {
        newQuery.parts = [change];
      } else {
        newQuery.parts = [];
      }

      openFilterRef.current = false;
      onChange?.({...newQuery, version: (query?.version ?? 0) + 1}, termsMemo, wizard, examplesMemo, similarity);
    }
  }

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <StyledAdvancedQuery ref={innerRef} {...innerProps}>
    {query ? <AdvancedQueryPart ref={partRef}
                                part={query}
                                depth={0}
                                first={true}
                                size={size}
                                error={error}
                                actions={actions}
                                canUpdate={canUpdate}
                                openFilter={openFilterRef.current}
                                filterGroups={filterGroupsMemo}
                                fieldData={fieldData}
                                required={required}
                                onError={onError}
                                onChange={handleChange}/> : null}
  </StyledAdvancedQuery>
});

AdvancedQuery.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  terms: PropTypes.array,
  wizard: PropTypes.string,
  examples: PropTypes.array,
  similarity: PropTypes.string,
  query: PropTypes.object,
  filterGroups: PropTypes.array,
  initialised: PropTypes.bool,
  fieldData: PropTypes.object,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  error: PropTypes.bool,
  success: PropTypes.bool,
  size: PropTypes.string,
  debounce: PropTypes.any,
  actions: PropTypes.array,
  onChange: PropTypes.func,
  onError: PropTypes.func
};

export default AdvancedQuery;
